// src/components/ConversationsManagement.js
//
import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs } from "firebase/firestore";
import Papa from "papaparse";

const toMillis = (value) => {
  if (!value) return null;
  if (value.toMillis) return value.toMillis();
  if (value.seconds) return value.seconds * 1000;
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const formatDate = (value) => {
  const ms = toMillis(value);
  if (ms === null) return "-";
  return new Date(ms).toLocaleDateString("pt-BR");
};

const ConversationsManagement = () => {
  const [conversations, setConversations] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const convSnap = await getDocs(collection(db, "conversations"));
        const userSnap = await getDocs(collection(db, "users"));

        const usersMap = {};
        userSnap.docs.forEach((d) => {
          usersMap[d.id] = d.data();
        });
        setUsers(usersMap);

        const rows = await Promise.all(
          convSnap.docs.map(async (d) => {
            const data = d.data();
            const messagesSnap = await getDocs(
              collection(db, `conversations/${d.id}/messages`),
            );
            const messages = messagesSnap.docs.map((m) => m.data());
            let lastMessageMs = null;
            messages.forEach((m) => {
              const ms = toMillis(m.createdAt);
              if (
                ms !== null &&
                (lastMessageMs === null || ms > lastMessageMs)
              ) {
                lastMessageMs = ms;
              }
            });
            return {
              id: d.id,
              users: Array.isArray(data.users) ? data.users : [],
              createdAt: data.createdAt,
              messageCount: messages.length,
              lastMessageAt: lastMessageMs ? new Date(lastMessageMs) : null,
            };
          }),
        );
        setConversations(rows);
      } catch (err) {
        console.error("Erro ao carregar conversas:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const sorted = useMemo(() => {
    return [...conversations].sort((a, b) => {
      const aMs = a.lastMessageAt
        ? a.lastMessageAt.getTime()
        : toMillis(a.createdAt) || 0;
      const bMs = b.lastMessageAt
        ? b.lastMessageAt.getTime()
        : toMillis(b.createdAt) || 0;
      return bMs - aMs;
    });
  }, [conversations]);

  const labelForUser = (uid) => {
    const u = users[uid];
    if (!u) return uid;
    const name = `${u.firstName || ""} ${u.lastName || ""}`.trim();
    return name || u.institutionName || u.email || uid;
  };

  const handleExport = () => {
    const exportData = sorted.map((c) => ({
      "ID da Conversa": c.id,
      "Participantes (IDs)": c.users.join(" | "),
      "Participantes (Nomes)": c.users.map(labelForUser).join(" | "),
      "Criada em": formatDate(c.createdAt),
      "Última mensagem": c.lastMessageAt
        ? c.lastMessageAt.toLocaleDateString("pt-BR")
        : "-",
      "Total de mensagens": c.messageCount,
    }));
    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "conversations_summary.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <div className="p-4 text-gray-600">Carregando conversas...</div>;
  }

  return (
    <div className="user-management">
      <h2>Conversas (resumo)</h2>
      <p className="text-sm text-gray-500 mb-3">
        Por privacidade, o conteúdo das mensagens não é exibido aqui — apenas
        metadados.
      </p>
      <div className="controls">
        <button className="export-button" onClick={handleExport}>
          Exportar resumo de conversas
        </button>
      </div>
      {sorted.length === 0 ? (
        <p className="text-gray-500">Nenhuma conversa registrada.</p>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 text-left">Participantes</th>
              <th className="p-2 text-left">Criada em</th>
              <th className="p-2 text-left">Última mensagem</th>
              <th className="p-2 text-left">Mensagens</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((c) => (
              <tr key={c.id} className="border-b hover:bg-gray-50">
                <td className="p-2">{c.users.map(labelForUser).join(" ↔ ")}</td>
                <td className="p-2">{formatDate(c.createdAt)}</td>
                <td className="p-2">
                  {c.lastMessageAt
                    ? c.lastMessageAt.toLocaleDateString("pt-BR")
                    : "-"}
                </td>
                <td className="p-2">{c.messageCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ConversationsManagement;
